import { useMemo, useCallback } from 'react';
import { Home } from 'lucide-react';
import { StepHeader } from '~/components/forms/StepHeader';
import { FamilyInfoChunk } from '~/components/form-chunks/FamilyInfoChunk';

interface AppFamilyInfoStepProps {
  formData: any;
  updateFormData: (path: string, value: any) => void;
  onReviewComplete?: (reviewed: boolean) => void;
  validationErrors?: { field: string; message: string }[];
}

/**
 * Bridges the application form's flat answer keys to the nested familyInfo
 * shape that FamilyInfoChunk expects.
 *
 * Application form schema stores:
 *   answers.familyInfo       → { address, familyName }
 *   answers.parentGuardians  → [{ firstName, lastName, ... }]
 *   answers.emergencyContacts → [...]
 *   answers.churchAffiliation → { churchName, ... }
 *
 * FamilyInfoChunk expects a single object with:
 *   { address, familyName, guardians, emergencyContacts, authorizedPickup, churchAffiliation }
 */
export function AppFamilyInfoStep({ formData, updateFormData, onReviewComplete, validationErrors = [] }: AppFamilyInfoStepProps) {
  // Compose a familyInfo object from the application form's flat answer keys
  const familyInfo = useMemo(() => ({
    ...(formData?.familyInfo || {}),
    guardians: formData?.parentGuardians || [],
    emergencyContacts: formData?.emergencyContacts || [],
    authorizedPickup: formData?.authorizedPickup || [],
    churchAffiliation: formData?.churchAffiliation || {},
  }), [formData?.familyInfo, formData?.parentGuardians, formData?.emergencyContacts, formData?.authorizedPickup, formData?.churchAffiliation]);

  // Decompose updates back to the correct top-level answer keys
  const handleUpdate = useCallback((updatedFamilyInfo: any) => {
    const { guardians, emergencyContacts, authorizedPickup, churchAffiliation, ...coreFamilyInfo } = updatedFamilyInfo;
    updateFormData('familyInfo', coreFamilyInfo);
    updateFormData('parentGuardians', guardians || []);
    updateFormData('emergencyContacts', emergencyContacts || []);
    updateFormData('authorizedPickup', authorizedPickup || []);
    updateFormData('churchAffiliation', churchAffiliation || {});
  }, [updateFormData]);

  return (
    <div className="space-y-6">
      <StepHeader
        icon={Home}
        title="Family Information"
        description="Please provide your family's contact information."
      />

      <FamilyInfoChunk
        familyInfo={familyInfo}
        onUpdate={handleUpdate}
        onReviewComplete={onReviewComplete}
        validationErrors={validationErrors}
      />
    </div>
  );
}
